import RedirectLink from '@Components/RedirectLink';
import TableTemplate, { TableConfig } from '@Dashboard/Charts/TableTemplate';
import { generateRedirectUrlToLog } from '@Log/shared';
import { useDashboardServicesStore } from '@Models/useDashboardServicesModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { ThreatType } from '@Utils/enums';
import { getServiceNamesByValues } from '@Utils/service';
import { getUtcTime } from '@Utils/time';
import { useColorMode, useColorStyle, Box, Text } from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { MAIL_SERVICES, NOT_MAIL_SERVICES } from '../utils';
import { ServiceNameEnum } from '@Types/index';
import { LogTypeEnum, SecurityFilterName } from '@Types/log';
import { useMemo } from 'react';

const AccountTakeoverRiskTable = (props: {
  insightsData: any;
  day: string;
  appendContextMenu?: {
    columnIndex: number;
    enabled: boolean;
    component: (user: string) => React.ReactNode;
  };
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const timeModel = useUtcOffsetStore();
  const servicesModel = useDashboardServicesStore();
  const services = servicesModel.enabledServices;

  const config: TableConfig[] = [
    { width: '20%', grow: 2, align: 'left', tooltip: true },
    { width: '10%', grow: 1, align: 'right' },
    { width: '10%', grow: 1, align: 'right' },
    { width: '10%', grow: 1, align: 'right' },
    { width: '10%', grow: 1, align: 'right' },
    { width: '10%', grow: 1, align: 'right' },
    { width: '20%', grow: 2, align: 'left' },
  ];
  const header = [
    t('riskUser.topUser.user'),
    t('riskUser.topUser.totalAmount'),
    t('riskUser.topUser.phishing'),
    t('riskUser.topUser.ransomware'),
    t('riskUser.topUser.maliciousFile'),
    t('riskUser.topUser.maliciousUrl'),
    t('riskUser.topUser.distributed'),
  ];

  const tableData = useMemo(() => {
    const getTotal = (x: any) => {
      return (
        (x.phishing || 0) +
        (x.ransomware || 0) +
        (x.malicious_file || 0) +
        (x.malicious_url || 0)
      );
    };

    const data = props.insightsData?.RiskyTopSenders?.[props.day] || [];
    data.sort((a: any, b: any) => {
      return getTotal(b) - getTotal(a);
    });

    return data.map((x: any) => {
      return [
        x.key || '',
        renderLink(
          getTotal(x),
          x.key || '',
          !!x.is_email_sender,
          props.day,
          [
            ThreatType.Phishing,
            ThreatType.Ransomware,
            ThreatType.MaliciousFile,
            ThreatType.MaliciousURL,
          ],
          services
        ),
        renderLink(
          x.phishing || 0,
          x.key || '',
          !!x.is_email_sender,
          props.day,
          [ThreatType.Phishing],
          services
        ),
        renderLink(
          x.ransomware || 0,
          x.key || '',
          !!x.is_email_sender,
          props.day,
          [ThreatType.Ransomware],
          services
        ),
        renderLink(
          x.malicious_file || 0,
          x.key || '',
          !!x.is_email_sender,
          props.day,
          [ThreatType.MaliciousFile],
          services
        ),
        renderLink(
          x.malicious_url || 0,
          x.key || '',
          !!x.is_email_sender,
          props.day,
          [ThreatType.MaliciousURL],
          services
        ),
        x.last_detected
          ? getUtcTime(
              x.last_detected,
              timeModel.offset,
              t('common.momentDateFormat')
            )
          : '',
      ];
    });
  }, [
    props.insightsData?.RiskyTopSenders,
    props.day,
    services,
    timeModel.offset,
    t,
  ]);

  return (
    <Box>
      <Box
        mb="24px"
        fontSize="18px"
        fontWeight="600"
        lineHeight="24px"
        color={colorStyle.color.primary}
      >
        {t('riskUser.topUser.sender')}
      </Box>
      <TableTemplate
        height="220px"
        config={config}
        header={header}
        data={tableData}
        appendContextMenu={props.appendContextMenu}
      />
    </Box>
  );
};

export default AccountTakeoverRiskTable;

function renderLink(
  count: number,
  key: string,
  isEmailSender: boolean,
  day: string,
  threatType: ThreatType[],
  services: ServiceNameEnum[]
) {
  if (count === 0) {
    return 0;
  }
  return (
    <RedirectLink
      href={`logs?${generateRedirectUrlToLog(
        [
          {
            field: 'threatType',
            values: threatType,
            type: 'terms',
          },
          isEmailSender
            ? {
                field: 'msgFrom',
                values: [key],
                type: 'terms',
              }
            : {
                field: 'violator',
                values: [key],
                type: 'terms',
              },
          // {
          //   field: 'mailDirection',
          //   values: ['out'],
          //   type: 'terms',
          // },
          isEmailSender
            ? {
                field: 'scanSource',
                values: getServiceNamesByValues(
                  services.filter((x) => MAIL_SERVICES.has(x))
                ),
                type: 'terms',
              }
            : {
                field: 'scanSource',
                values: getServiceNamesByValues(
                  services.filter((x) => NOT_MAIL_SERVICES.has(x))
                ),
                type: 'terms',
              },
          {
            field: 'securityFilterName',
            values: [
              SecurityFilterName.AdvancedSpamProtection,
              SecurityFilterName.MalwareScanning,
              SecurityFilterName.VirtualAnalyzer,
              SecurityFilterName.WebReputation,
              SecurityFilterName.ContentFiltering,
            ],
            type: 'terms',
          },
        ],
        day,
        LogTypeEnum.DETECTION
      )}`}
    >
      <Text>{count}</Text>
    </RedirectLink>
  );
}
